import React, { useCallback, useEffect, useState } from 'react';
import {
  attachDetachedDraft,
  listDetachedDrafts,
  readDetachedDraft,
} from '../../api/sceneWriting';
import {
  DETACHED_ACCEPTED,
  DETACHED_ATTACH,
  DETACHED_ATTACH_LABEL,
  DETACHED_ATTACHED,
  DETACHED_HEADING,
  DETACHED_HIDE,
  DETACHED_INTRO,
  DETACHED_PICK,
  DETACHED_READ,
} from '../../utils/writerCopy';

/**
 * Migration 183: when a scene is deleted (Replace, re-approve, outline edits),
 * its drafts stay, unattached, with the scene's name. Nothing was deleted;
 * the writer reads them here and attaches each to a scene again.
 */
export function wordsApprox(chars) {
  const words = Math.max(1, Math.round((Number(chars) || 0) / 6));
  return `about ${words.toLocaleString()} word${words === 1 ? '' : 's'}`;
}

export default function DetachedDrafts({ storyId, scenes = [], onAttached }) {
  const [drafts, setDrafts] = useState([]);
  const [open, setOpen] = useState({});
  const [targets, setTargets] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [note, setNote] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!storyId) return;
    try {
      const res = await listDetachedDrafts(storyId);
      setDrafts(res?.drafts || []);
    } catch {
      setDrafts([]);
    }
  }, [storyId]);

  useEffect(() => {
    setOpen({});
    setTargets({});
    setNote(null);
    load();
  }, [load]);

  if (!drafts.length && !note) return null;

  const toggleRead = async (draft) => {
    if (open[draft.id] !== undefined) {
      setOpen((prev) => {
        const next = { ...prev };
        delete next[draft.id];
        return next;
      });
      return;
    }
    try {
      const full = await readDetachedDraft(storyId, draft.id);
      setOpen((prev) => ({ ...prev, [draft.id]: full?.generated_text || draft.excerpt }));
    } catch (err) {
      setError(err?.message || 'Could not open that draft.');
    }
  };

  const attach = async (draft) => {
    const sceneId = targets[draft.id];
    if (!sceneId) return;
    setBusyId(draft.id);
    setError(null);
    try {
      await attachDetachedDraft(storyId, draft.id, sceneId);
      const scene = scenes.find((s) => String(s.id) === String(sceneId));
      setNote(DETACHED_ATTACHED(draft.scene_name, scene?.display_name || 'that scene'));
      await load();
      onAttached?.(sceneId);
    } catch (err) {
      setError(err?.message || 'Could not attach that draft.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="sw-detached" aria-labelledby="sw-detached-heading" data-testid="sw-detached-drafts">
      <h3 id="sw-detached-heading">{DETACHED_HEADING(drafts.length)}</h3>
      {note && <p className="sw-detached__note" role="status">{note}</p>}
      {error && <p className="sw-detached__error" role="alert">{error}</p>}
      {drafts.length > 0 && <p className="sw-detached__intro">{DETACHED_INTRO}</p>}
      <ul className="sw-detached__list">
        {drafts.map((draft) => {
          const selectId = `sw-detached-target-${draft.id}`;
          return (
            <li key={draft.id} className="sw-detached__item">
              <div className="sw-detached__head">
                <span className="sw-detached__name">{draft.scene_name}</span>
                {draft.accepted && <span className="sw-detached__badge">{DETACHED_ACCEPTED}</span>}
                <span className="sw-detached__size">{wordsApprox(draft.chars)}</span>
              </div>
              <p className="sw-detached__excerpt">{open[draft.id] ?? draft.excerpt}</p>
              <div className="sw-detached__actions">
                <button type="button" className="sw-btn" onClick={() => toggleRead(draft)}>
                  {open[draft.id] !== undefined ? DETACHED_HIDE : DETACHED_READ}
                </button>
                <label htmlFor={selectId} className="sw-detached__label">{DETACHED_ATTACH_LABEL}</label>
                <select
                  id={selectId}
                  value={targets[draft.id] || ''}
                  onChange={(e) => setTargets((prev) => ({ ...prev, [draft.id]: e.target.value }))}
                >
                  <option value="">{DETACHED_PICK}</option>
                  {scenes.map((scene) => (
                    <option key={scene.id} value={scene.id}>{scene.display_name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  className="sw-btn"
                  disabled={!targets[draft.id] || busyId === draft.id}
                  onClick={() => attach(draft)}
                >
                  {DETACHED_ATTACH}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
